import { type Dispatch, type SetStateAction, useState } from 'react';

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Checkbox, color, IcomoonIcon, Icon, Table } from '@wf/components';
import { Label } from '@wf/sw-ui-kit';

import { LicenseField } from '../../LicenseField';

import { downloadAttachment } from '../../../../../services/attachmentService';

import { ArrayFields, AttachedDocumentsTabFields } from '../../../types/fieldEnums';
import useGenerateParams from '../../../hooks/useGenerateParams';
import { type IAttachmentFormValues } from '../types';

import { filterSelectOptionWithTag } from '../../../../../helpers/filterSelectOptionWithTag';
import { RimmEntityType } from '../../../../rimm/data/rimmEntityEnums';
import openFileInNewTab from '../../../../../helpers/openFileInNewTab';
import { getIconName } from '../../../../../helpers';

import styles from './AttachmentsTableForm.module.scss';

interface IAttachmentsTableFormProps {
  deleteCallback: () => void;
  mode: 'default' | 'import';
  checkedItemIds: Set<number>;
  setCheckedItemIds: Dispatch<SetStateAction<Set<number>>>;
}

function AttachmentsTableForm({
  deleteCallback,
  mode = 'default',
  checkedItemIds,
  setCheckedItemIds,
}: IAttachmentsTableFormProps) {
  const { t } = useTranslation();
  const { parentId } = useGenerateParams();

  const { control } = useFormContext();

  const [isDownloading, setIsDownloading] = useState(false);

  const { remove } = useFieldArray({
    name: ArrayFields.attachedDocuments,
    control,
  });

  const fields = useWatch({ name: ArrayFields.attachedDocuments }) as IAttachmentFormValues[];

  const handleDeleteAttachment = (index: number) => {
    if (fields?.length === 1) {
      deleteCallback();
    } else {
      remove(index);
    }
  };

  const handleOpenAttachment = async (fileURL: string, attachmentId?: number) => {
    try {
      setIsDownloading(true);
      const { data } = await downloadAttachment({
        fileURL,
        lpcoId: attachmentId ? (parentId as string) : undefined,
        attachmentId,
        isSaved: !!attachmentId,
      });
      openFileInNewTab(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCheck = (checked: boolean, id: number): void => {
    if (id) {
      const newCheckedItemIds = new Set(checkedItemIds);

      if (checked) {
        newCheckedItemIds.add(id);
      } else {
        newCheckedItemIds.delete(id);
      }

      setCheckedItemIds(newCheckedItemIds);
    }
  };

  const handleCheckAll = () => {
    setCheckedItemIds(checkedItemIds.size ? new Set() : new Set(fields.map(({ id }) => id as number)));
  };

  return (
    <Table>
      <Table.Grid>
        <Table.Header>
          <Table.Row className={styles.tableRow}>
            {mode === 'import' && (
              <Table.HeaderCell className={styles.checkboxCell}>
                <Checkbox
                  intermediate={checkedItemIds.size < fields.length}
                  checked={checkedItemIds.size > 0}
                  onChange={handleCheckAll}
                />
              </Table.HeaderCell>
            )}
            <Table.HeaderCell className={styles.fileCell}>{t('File Name')}</Table.HeaderCell>
            <Table.HeaderCell className={styles.codeCell}>
              <Label helpText={t('A unique identifier for the attached document')}>{t('Code')}</Label>
            </Table.HeaderCell>
            <Table.HeaderCell className={styles.refCell}>
              <Label helpText={t('A reference number attributed to the attached document')}>
                {t('Reference Number')}
              </Label>
            </Table.HeaderCell>
            <Table.HeaderCell className={styles.dateCell}>
              <Label helpText={t('The date when the attached document was issued or created')}>{t('Date')}</Label>
            </Table.HeaderCell>
            {mode === 'default' && <Table.HeaderCell className={styles.actionCell} />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {fields.map((attachment, index) => (
            <Table.Row key={attachment.fileUrl} className={styles.tableRow}>
              {mode === 'import' && (
                <Table.Cell className={styles.checkboxCell}>
                  <Checkbox
                    onChange={(checked) => handleCheck(checked, attachment.id as number)}
                    checked={checkedItemIds.has(attachment.id as number)}
                  />
                </Table.Cell>
              )}
              <Table.Cell className={styles.fileCell}>
                {attachment.fileName && (
                  <div
                    className={cn(styles.fileName, {
                      [styles.loading as string]: isDownloading,
                    })}
                    onClick={() => handleOpenAttachment(attachment.fileUrl as string, attachment.id)}
                    title={attachment.fileName}
                    role="button"
                    tabIndex={0}
                  >
                    <IcomoonIcon name={getIconName(attachment.fileName)} className={styles.fileIcon} />
                    {attachment.fileName}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell className={styles.codeCell}>
                <LicenseField.RimmSelectField
                  styles={{
                    menu: {
                      width: '170%',
                      insetInlineStart: 0,
                    },
                  }}
                  name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.code}`}
                  filterOption={filterSelectOptionWithTag}
                  entity={RimmEntityType.attachedDocument}
                  isClearable={false}
                  size="sm"
                  withTag
                />
              </Table.Cell>
              <Table.Cell className={styles.refCell}>
                <LicenseField.TextField
                  name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.referenceNumber}`}
                  size="sm"
                />
              </Table.Cell>
              <Table.Cell className={styles.dateCell}>
                <LicenseField.DateField
                  name={`${ArrayFields.attachedDocuments}.${index}.${AttachedDocumentsTabFields.attachmentDate}`}
                  maxDate={new Date().setHours(23, 59, 59, 999)}
                  isClearable={false}
                  size="sm"
                />
              </Table.Cell>
              {mode === 'default' && (
                <Table.Cell className={styles.actionCell} align="right">
                  <Icon
                    onClick={() => handleDeleteAttachment(index)}
                    className={styles.deleteBtn}
                    color={color('typography')}
                    name="ic_delete"
                  />
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Grid>
    </Table>
  );
}

export default AttachmentsTableForm;
